import {
	addMonths,
	isAfter,
	isBefore,
	isEqual,
	startOfDay,
	startOfMonth,
	startOfYear,
} from "date-fns";
import ModuleSetting from "../../../../models/ModuleSetting";
import {
	ModuleSettingAmounts,
	Category,
	NewModuleSetting,
	RootProductId,
	UpdatedModuleSetting,
	UserWithBalanceAccounts,
} from "@models";
import { getDateFlags } from "@util";
import { formatDateMonth } from "@service/util";
import { formatDate } from "@components/forms/datePickerUtils";

export function getDefaultBudgetAmounts(
	category: Category
): ModuleSettingAmounts {
	// TODO: get default values from somewhere
	return {
		category: category.id,
		maxAmountDay: null,
		maxAmountMonth: category.maxAmountMonth ?? 2000,
	};
}

export function escapeCategoryId(categoryId: string): string {
	// If it already contains an underscore, return as-is
	// (either naturally contains underscore like 'travel_budget' or already escaped)
	if (categoryId.includes("_")) {
		return categoryId;
	}
	return categoryId.replace(/\./g, "_");
}

export function unescapeCategoryId(escapedCategoryId: string): string {
	return escapedCategoryId.replace(/_/g, ".");
}

export function splitModuleSettings(moduleSettings: ModuleSetting[]) {
	const activeModuleSettings: ModuleSetting[] = [];
	const expiredModuleSettings: ModuleSetting[] = [];
	const futureModuleSettings: ModuleSetting[] = [];

	moduleSettings.forEach((moduleSetting) => {
		const { isActive, isExpired, isInFuture } = getDateFlags(
			moduleSetting.availableFrom,
			moduleSetting.availableUntil
		);
		if (isActive) {
			activeModuleSettings.push(moduleSetting);
		} else if (isExpired) {
			expiredModuleSettings.push(moduleSetting);
		} else if (isInFuture) {
			futureModuleSettings.push(moduleSetting);
		}
	});

	const byStartDateDesc = (a: ModuleSetting, b: ModuleSetting) =>
		(b.availableFrom as any) - (a.availableFrom as any);
	activeModuleSettings.sort(byStartDateDesc);
	expiredModuleSettings.sort(byStartDateDesc);
	futureModuleSettings.sort(byStartDateDesc);

	return { activeModuleSettings, expiredModuleSettings, futureModuleSettings };
}

export function isNewModuleSetting(
	b: NewModuleSetting | UpdatedModuleSetting
): b is NewModuleSetting {
	return !("moduleSettingId" in b);
}

export function isDateRangeWithin(
	from: Date | null,
	until: Date | null,
	moduleSetting: NewModuleSetting | UpdatedModuleSetting
) {
	const { availableFrom, availableUntil } = moduleSetting;

	if (!availableFrom) {
		if (!from) return true;
		if (!availableUntil) return true;
		return (
			isBefore(startOfDay(from), availableUntil) ||
			isEqual(startOfDay(from), availableUntil)
		);
	}

	if (!availableUntil) {
		if (!until) return true;

		return isAfter(startOfDay(until), availableFrom);
	}

	if (until && isBefore(startOfDay(until), availableFrom)) return false;

	if (from && isAfter(startOfDay(from), availableUntil)) return false;

	return true;
}

export function canConfigureBalanceAccount(rootProductId: RootProductId) {
	return (
		rootProductId === "spend" ||
		rootProductId === "web" ||
		rootProductId === "lunch"
	);
}

export function getBalanceAccount(
	user: UserWithBalanceAccounts,
	moduleSetting: ModuleSetting | NewModuleSetting
) {
	return user.balanceAccounts.find(
		(ba) => ba.budgetCategory === moduleSetting.category
	);
}

const YEARLY_BUDGETS: RootProductId[] = ["fitAndRelax", "homeAndWork"];

export function isYearlyBudget(categoryId: string | undefined | null): boolean {
	return !!categoryId && YEARLY_BUDGETS.some((c) => categoryId.startsWith(c));
}

export function startDateOnSplit(productId: RootProductId): Date {
	return isYearlyBudget(productId)
		? startOfYear(new Date())
		: addMonths(startOfMonth(new Date()), 1);
}

export const collisionErrorMessage = {
	default:
		"Im angegebenen Zeitraum liegen bereits andere Budgets. Ändern Sie den Verfügbarkeitszeitraum oder passen Sie die anderen Budgets an.",
	monthCollision: (availableFrom: Date) =>
		`Im Monat ${formatDateMonth(
			availableFrom
		)} liegt bereits ein anderes Budget. Es dürfen keine zwei Budgets in demselben Monat liegen.`,
	monthCollisionChangedEndDate: (availableFrom: Date, endDate: Date) =>
		`In dem angegebenen Zeitraum liegt ein anderes Budget. Wenn Sie ein neues Budget erzeugen, wird das bestehende am ${formatDate(
			endDate
		)} beendet und ab dem ${formatDate(availableFrom)} ein neues erzeugt.`,
	annualCollision:
		"Es darf pro Jahr nur max. eine Moduleinstellung geben. Überprüfen Sie andere Budgets dieses Produkts und legen Sie andere Gültigkeitsbereiche fest.",
};
